import * as React from 'react';
import {
  useState,
  useCallback,
  useRef,
  ReactNode,
  HtmlHTMLAttributes,
  useContext,
} from 'react';
import { clsx } from 'clsx';
import { Menu, styled } from '@mui/material';
import { FilterContext } from 'ra-ui-materialui';
import { CloseCircleOutlined } from '@ant-design/icons';
import ContentFilter from '@mui/icons-material/FilterList';
import { useListContext, useResourceContext } from 'ra-core';
import cls from './FilterButton.module.css';

import { Button } from './Button';
import { FilterButtonMenuItem } from './FilterButtonMenuItem';

export const FilterButton = (props: FilterButtonProps): JSX.Element => {
  const {
    filters: filtersProp,
    className,
    disableSaveQuery,
    ...rest
  } = props;
  const filters = useContext(FilterContext) || filtersProp;
  const resource = useResourceContext(props);
  const {
    displayedFilters = {},
    filterValues,
    setFilters,
    showFilter,
    hideFilter,
  } = useListContext(props);
  const [open, setOpen] = useState(false);
  const anchorEl = useRef();

  if (filters === undefined) {
    throw new Error(
      'The <FilterButton> component requires the <List filters> prop to be set',
    );
  }

  const getLabel = (item: any) => {
    if (item == null) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (typeof item === 'object') {
      if (item.props && (typeof item.props.label === 'string' || typeof item.props.label === 'number')) {
        return String(item.props.label);
      }
      if (item.props && !item.props.label) {
        return item.key;
      }
    }
    return '';
  };

  const sortByLabel = (arr: any, order = 'asc') => {
    return [...arr].sort((a, b) => {
      const A = getLabel(a).trim();
      const B = getLabel(b).trim();

      const emptyA = A === '';
      const emptyB = B === '';

      if (emptyA && emptyB) return 0;
      if (emptyA) return order === 'asc' ? 1 : -1;
      if (emptyB) return order === 'asc' ? -1 : 1;

      const cmp = A.localeCompare(B, undefined, { sensitivity: 'base' });
      return order === 'asc' ? cmp : -cmp;
    });
  };

  const hiddenFilters = sortByLabel(filters)
    .filter((filterElement: any) => !filterElement.props.alwaysOn);

  const handleClickButton = useCallback(
    event => {
      // This prevents ghost click.
      event.preventDefault();
      setOpen(true);
      anchorEl.current = event.currentTarget;
    },
    [anchorEl, setOpen],
  );

  const handleRequestClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleToggleShow = ({ source, defaultValue }: {source: string, defaultValue?: string}) => {
    if (displayedFilters[source]) {
      hideFilter(source);
    } else {
      showFilter(source, defaultValue === '' ? undefined : defaultValue);
    }
  };

  const handleClearFilters = () => {
    const displayedFiltersKeys = Object.keys(displayedFilters);
    displayedFiltersKeys.forEach(key => {
      hideFilter(key);
    });
    setFilters({}, {});
  };

  const activeFiltersCount = Object.values(displayedFilters).filter(
    value => value !== undefined && value !== null && value !== '',
  ).length;
  const filterValuesCount = Object.values(filterValues).filter(
    value => value !== undefined && value !== null && value !== '',
  ).length;

  const isFiltersApplied = activeFiltersCount > 0 || filterValuesCount > 0;

  return (
    <Root className={className} {...sanitizeRestProps(rest as any)}>
      {isFiltersApplied && (
        <FilterClear onClick={handleClearFilters}>
          <CloseCircleOutlined />
        </FilterClear>
      )}
      <Button
        className="add-filter"
        label="ra.action.add_filter"
        aria-haspopup="true"
        onClick={handleClickButton}
      >
        <ContentFilter />
      </Button>
      <Menu
        open={open}
        anchorEl={anchorEl.current}
        onClose={handleRequestClose}
        className={clsx(
          cls.filterMenu,
          hiddenFilters.length > 14
            ? hiddenFilters.length > 28 ? cls.threeColumns : cls.twoColumns
            : cls.oneColumn,
        )}
      >
        {hiddenFilters
          .map((filterElement: any, index) => {
            const isChecked = displayedFilters[filterElement.props.source] ? true : false;
            return (
              <FilterButtonMenuItem
                key={filterElement.props.source}
                filter={filterElement}
                resource={resource}
                onShow={handleToggleShow}
                autoFocus={index === 0}
                isChecked={isChecked}
              />
            );
          })}
      </Menu>
    </Root>
  );
};

/* eslint-disable @typescript-eslint/no-unused-vars */
const sanitizeRestProps = ({
  displayedFilters = null,
  filterValues = null,
  showFilter = null,
  ...rest
}) => rest;

/* eslint-enable @typescript-eslint/no-unused-vars */

export interface FilterButtonProps extends HtmlHTMLAttributes<HTMLDivElement> {
  className?: string;
  resource?: string;
  filterValues?: any;
  showFilter?: (filterName: string, defaultValue: any) => void;
  displayedFilters?: any;
  filters?: ReactNode[];
  disableSaveQuery?: boolean;
}

const PREFIX = 'RaFilterButton';

const Root = styled('div', {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})({
  display: 'inline-block',
  position: 'relative',
});

const FilterClear = styled('div')({
  position: 'absolute',
  bottom: 12,
  left: 28,
  transform: 'translate(-50%, 50%)',
  zIndex: 1,
  width: '20px',
  height: '20px',
  color: 'var(--primary-color)',
  cursor: 'pointer',
});

